import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileUploadZone } from '@/components/files/FileUploadZone';
import { OrgOneDriveShares } from '@/components/files/OrgOneDriveShares';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useViewerStore } from '@/stores/viewer/viewer-store';
import { useFileStore } from '@/stores/files/file-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { AlertCircle, Cloud, FolderOpen, Upload, X } from 'lucide-react';

interface FileStorageModalProps {
  isOpen: boolean;
  onClose: () => void;
  componentId: string;
  currentFolder?: string;
}

function formatBytes(bytes: number | null | undefined): string {
  const safeBytes = typeof bytes === 'number' && Number.isFinite(bytes) ? Math.max(bytes, 0) : 0;
  if (safeBytes >= 1024 * 1024 * 1024) return `${(safeBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (safeBytes >= 1024 * 1024) return `${(safeBytes / (1024 * 1024)).toFixed(1)} MB`;
  if (safeBytes >= 1024) return `${(safeBytes / 1024).toFixed(0)} KB`;
  return `${safeBytes} B`;
}

function formatQuota(bytes: number | null | undefined): string {
  return typeof bytes === 'number' && Number.isFinite(bytes) && bytes > 0
    ? formatBytes(bytes)
    : 'No quota';
}

function resolveUsagePercentage(
  totalSize: number | null | undefined,
  quota: number | null | undefined,
  usagePercentage: number | null | undefined,
): number {
  if (typeof usagePercentage === 'number' && Number.isFinite(usagePercentage)) {
    return Math.max(0, Math.min(usagePercentage, 100));
  }

  if (
    typeof totalSize === 'number' &&
    Number.isFinite(totalSize) &&
    typeof quota === 'number' &&
    Number.isFinite(quota) &&
    quota > 0
  ) {
    return Math.max(0, Math.min((totalSize / quota) * 100, 100));
  }

  return 0;
}

export function FileStorageModal({ isOpen, onClose, componentId, currentFolder }: FileStorageModalProps) {
  // Dual-store component lookup (same pattern as AddTableRowModal)
  const editorComponent = useEditorStore((s) => s.components.find((c) => c.id === componentId));
  const viewerComponent = useViewerStore((s) => s.componentMap.get(componentId));
  const component = editorComponent || viewerComponent;

  const viewerOrgSlug = useViewerStore((s) => s.viewerOrgSlug);
  const editorOrgSlug = useOrganizationStore((s) => s.currentContext?.orgSlug);
  const orgSlug = viewerOrgSlug || editorOrgSlug || null;
  const { usage, fetchUsage } = useFileStore();

  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'files' | 'onedrive'>('files');

  // Extract component properties
  const props = component?.properties as Record<string, unknown> | undefined;
  const title = (props?.title as string) || 'File Storage';
  const uploadEnabled = props?.uploadEnabled !== false;
  const defaultScope = (props?.defaultScope as 'private' | 'project' | 'shared') || undefined;
  const encryptionDefault = (props?.encryptionDefault as 'none' | 'org') || undefined;
  const showQuotaBar = props?.showQuotaBar === true;
  const allowedFileTypes = Array.isArray(props?.allowedFileTypes) ? props.allowedFileTypes as string[] : [];
  const maxFiles = (props?.maxFiles as number) || 20;
  const defaultFolder = (props?.defaultFolder as string) || '';

  // Dual-source project context (viewer or editor)
  const viewerProject = useViewerStore((s) => s.project);
  const editorProject = useEditorStore((s) => s.project);
  const projectId = viewerProject?.id || editorProject?.id;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setRejectedFiles([]);
      setActiveTab('files');
    }
  }, [isOpen]);

  // Auto-clear rejected files warning after 5s
  useEffect(() => {
    if (rejectedFiles.length === 0) return;
    const timer = setTimeout(() => setRejectedFiles([]), 5000);
    return () => clearTimeout(timer);
  }, [rejectedFiles]);

  // Log on mount/open
  useEffect(() => {
    if (isOpen) {
      console.log('[FileStorage:Modal] Mounted', { isOpen, componentId, orgSlug, componentFound: !!component });
    }
  }, [isOpen, componentId, orgSlug, component]);

  // Fetch quota usage when modal opens
  useEffect(() => {
    if (!isOpen || !orgSlug || !showQuotaBar) return;
    fetchUsage(orgSlug);
  }, [isOpen, orgSlug, showQuotaBar, fetchUsage]);

  const handleUploadComplete = useCallback(() => {
    console.log('[FileStorage:Modal] Upload complete');
    if (!orgSlug) return;
    if (showQuotaBar) fetchUsage(orgSlug);
    // Signal preview components to re-fetch their file lists
    window.dispatchEvent(new CustomEvent('file-storage-upload-complete'));
  }, [orgSlug, fetchUsage, showQuotaBar]);

  const handleFilesRejected = useCallback((filenames: string[]) => {
    setRejectedFiles(filenames);
  }, []);

  const usagePercentage = usage
    ? resolveUsagePercentage(usage.totalSize, usage.quota, usage.usagePercentage)
    : 0;

  // Missing org slug — show error state
  if (isOpen && !orgSlug) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent variant="light" className="max-w-md p-0 bg-card text-card-foreground border-border">
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-5 w-5 text-destructive" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-foreground">{title}</h3>
                <p className="text-sm text-muted-foreground">Unable to access file storage</p>
              </div>
            </div>
          </div>
          <div className="border-t border-border" />
          <div className="px-6 py-5">
            <div className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
              <p className="text-sm text-destructive">
                Organization context is unavailable. File operations require an organization slug in the URL.
              </p>
            </div>
          </div>
          <div className="flex justify-end px-6 py-4 border-t border-border bg-muted/30">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="light" className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0 bg-card text-card-foreground border-border">
        <div
          className="flex flex-col flex-1 overflow-hidden"
          onDragOver={(e: React.DragEvent) => e.preventDefault()}
          onDrop={(e: React.DragEvent) => e.preventDefault()}
        >
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Upload className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-foreground">{title}</h3>
              <p className="text-sm text-muted-foreground">
                Upload and manage your project files
              </p>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="border-t border-border" />

        {/* Tab Buttons */}
        <div className="flex gap-1 px-6 border-b border-border" data-testid="file-storage-tabs">
          <button
            onClick={() => setActiveTab('files')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition-colors ${
              activeTab === 'files'
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="file-storage-tab-files"
          >
            <FolderOpen className="w-4 h-4" />
            Files
          </button>
          <button
            onClick={() => setActiveTab('onedrive')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition-colors ${
              activeTab === 'onedrive'
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="file-storage-tab-onedrive"
          >
            <Cloud className="w-4 h-4" />
            OneDrive
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'files' ? (
          <div className="flex flex-col gap-4 flex-1 overflow-hidden px-6 py-5">
            {/* Upload Zone */}
            {uploadEnabled && orgSlug && (
              <FileUploadZone
                orgSlug={orgSlug}
                scope={defaultScope}
                projectId={projectId}
                defaultEncryption={encryptionDefault}
                folder={currentFolder ?? defaultFolder}
                onUploadComplete={handleUploadComplete}
                allowedFileTypes={allowedFileTypes}
                onFilesRejected={handleFilesRejected}
                maxFiles={maxFiles}
              />
            )}

            {/* Rejected Files Warning */}
            {rejectedFiles.length > 0 && (
              <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-amber-200 flex-1">
                  Rejected: {rejectedFiles.join(', ')} — file type not allowed
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={() => setRejectedFiles([])}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}

            {/* Quota Bar */}
            {showQuotaBar && usage && (
              <div className="px-1 py-2 border-t border-border">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>Storage: {formatBytes(usage.totalSize)} / {formatQuota(usage.quota)}</span>
                  <span>{Math.round(usagePercentage)}%</span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      usagePercentage > 90 ? 'bg-red-500' :
                      usagePercentage > 75 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${usagePercentage}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          /* OneDrive Tab Content */
          <div className="flex-1 overflow-y-auto min-h-[200px] px-6 py-5">
            {orgSlug ? (
              <OrgOneDriveShares orgSlug={orgSlug} />
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Cloud className="w-8 h-8 mb-2 opacity-50" />
                <p className="text-sm">OneDrive is not configured</p>
              </div>
            )}
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
